import { MachineData } from './types';
import { handleApiResponse } from '../common';
import { getApiBaseUrl, API_VERSION } from '../index';

/**
 * Machines API - fetch registered workstations
 */
export const machinesApi = {
  /**
   * List all machines with optional filters
   * @param {Object} options
   * @param {string} options.status - Filter by status: 'online', 'offline', 'error'
   * @param {boolean} options.includeCapabilities - Include capability data
   * @returns {Promise<{machines: MachineData[], total: number}>}
   */
  async list({ status = null, includeCapabilities = true } = {}) {
    try {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (includeCapabilities) params.append('include_capabilities', 'true');
      
      const queryString = params.toString();
      const url = `${getApiBaseUrl()}${API_VERSION}/machines${queryString ? '?' + queryString : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      
      // Transform and sort machines
      const machines = MachineData.fromApiResponseList(data.machines || []);
      const sortedMachines = MachineData.sort(machines);
      
      return {
        machines: sortedMachines,
        total: data.total || sortedMachines.length
      };
    } catch (error) {
      console.error('Failed to fetch machines:', error);
      throw error;
    }
  },

  /**
   * Get a single machine by ID
   * @param {number} machineId
   * @returns {Promise<MachineData>}
   */
  async get(machineId) {
    try {
      const response = await fetch(
        `${getApiBaseUrl()}${API_VERSION}/machines/${machineId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );
      
      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      
      return MachineData.fromApiResponse(data);
    } catch (error) {
      console.error(`Failed to fetch machine ${machineId}:`, error);
      throw error;
    }
  },

  /**
   * Get machines available for a specific launcher
   * @param {string} launcherName
   * @param {string} profile - Optional profile name filter
   * @returns {Promise<{launcher_name: string, profile_name: string|null, machines: MachineData[], total: number}>}
   */
  async getAvailable(launcherName, profile = null) {
    try {
      const params = profile ? `?profile=${encodeURIComponent(profile)}` : '';
      const response = await fetch(
        `${getApiBaseUrl()}${API_VERSION}/machines/available/${encodeURIComponent(launcherName)}${params}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );
      
      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      
      const machines = MachineData.fromApiResponseList(data.machines || []);
      const sortedMachines = MachineData.sort(machines);
      
      return {
        launcher_name: data.launcher_name,
        profile_name: data.profile_name,
        machines: sortedMachines,
        total: data.total || sortedMachines.length
      };
    } catch (error) {
      console.error(`Failed to fetch available machines for ${launcherName}:`, error);
      throw error;
    }
  }
};

export default machinesApi;







